import { useState, useEffect } from "react";
import API from "../../../api/index";
import {
  Typography,
  Box,
  Grid,
  Dialog,
  DialogContent,
  IconButton,
  Button,
  Stack,
  Skeleton,
} from "@mui/material";
import { DataGridPro } from "@mui/x-data-grid-pro";
import { dataGridStyle, boxStyle } from "../../../theme";
import CloseIcon from "@mui/icons-material/Close";
import {
  TOEstablishTheColorOfLog,
  renderTypeCell,
} from "../../../components/ImportantFunctions";
import { useNavigate } from "react-router-dom";
import BugReportIcon from "@mui/icons-material/BugReport";
import KeyIcon from "@mui/icons-material/Key";
import ShieldIcon from "@mui/icons-material/Shield";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

/* ── SVG Circular Gauge ────────────────────────────────── */
const CircularGauge = ({ value, color, size = 160 }) => {
  const strokeWidth = 10;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (value / 100) * circumference;

  return (
    <Box sx={{ position: "relative", width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="rgba(255, 255, 255, 0.06)"
          strokeWidth={strokeWidth}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          style={{
            transition: "stroke-dashoffset 0.8s ease, stroke 0.5s ease",
          }}
        />
      </svg>

      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography
          sx={{ fontSize: 32, fontWeight: 700, color, lineHeight: 1 }}
        >
          {value}
        </Typography>
        <Typography sx={{ fontSize: 12, color: "#888", mt: 0.5 }}>
          / 100
        </Typography>
      </Box>
    </Box>
  );
};

/* ── Stat row component ───────────────────────────────── */
const StatRow = ({ icon, label, value, onClick }) => (
  <Box
    onClick={onClick}
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      py: 1.5,
      px: 2,
      cursor: "pointer",
      borderRadius: "10px",
      transition: "background-color 0.2s ease",
      "&:hover": { backgroundColor: "rgba(255,255,255,0.04)" },
    }}
  >
    <Stack direction="row" spacing={1.5} alignItems="center">
      <Box
        sx={{
          width: 30,
          height: 30,
          borderRadius: "8px",
          backgroundColor: "rgba(255, 255, 255, 0.06)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {icon}
      </Box>
      <Typography sx={{ fontSize: 13, color: "#aaa" }}>{label}</Typography>
    </Stack>

    <Typography sx={{ fontSize: 16, fontWeight: 600, color: "#D1C266" }}>
      {value}
    </Typography>
  </Box>
);

/* ── Main Component ───────────────────────────────────── */
function RatingGraph({ setVisibleGlobe, data }) {
  const navigate = useNavigate();

  const [rating, setRating] = useState(0);
  const [displayedRating, setDisplayedRating] = useState(0);
  const [cve, setCve] = useState(0);
  const [def, setDefault] = useState(0);
  const [vuln, setVuln] = useState(0);
  const [expire, setExpire] = useState(0);
  const [barColor, setBarColor] = useState("#77D168");
  const [keyWord, setKeyWord] = useState("");
  const [dataTable, setDataTable] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [charged, setCharged] = useState(false);
  const [loading, setLoading] = useState(true);

  const calcolaColore = (valore) => {
    const coloreBaseVerde = [119, 209, 104];
    const coloreBaseRosso = [147, 25, 29];

    const interpolate = (start, end, factor) =>
      start + (end - start) * factor;

    const factor = valore / 100;
    const rosso = Math.round(
      interpolate(coloreBaseVerde[0], coloreBaseRosso[0], 1 - factor)
    );
    const verde = Math.round(
      interpolate(coloreBaseVerde[1], coloreBaseRosso[1], 1 - factor)
    );
    const blu = Math.round(
      interpolate(coloreBaseVerde[2], coloreBaseRosso[2], 1 - factor)
    );

    const toHex = (value) => {
      const hex = value.toString(16);
      return hex.length === 1 ? `0${hex}` : hex;
    };

    return `#${toHex(rosso)}${toHex(verde)}${toHex(blu)}`;
  };

  useEffect(() => {
    if (data !== undefined) {
      if (data) {
        setRating(data.score);
        setCve(data.cve);
        setDefault(data.default);
        setVuln(data.vuln);
        setExpire(data.expired);
      }
      setLoading(false);
      return;
    }
    const controller = new AbortController()
    API.getRating(undefined, controller.signal)
      .then((response) => {
        setRating(response.data.score);
        setCve(response.data.cve);
        setDefault(response.data.default);
        setVuln(response.data.vuln);
        setExpire(response.data.expired);
      })
      .catch((err) => {
        if (err.name !== 'CanceledError') console.error("[RatingGraph] Errore nel recupero del rating:", err);
      })
      .finally(() => setLoading(false));
    return () => controller.abort()
  }, [data]);

  useEffect(() => {
    setBarColor(calcolaColore(rating));
  }, [rating]);

  useEffect(() => {
    let currentRating = 0;
    const increment = rating / 30;

    const intervalId = setInterval(() => {
      currentRating += increment;

      if (currentRating >= rating) {
        currentRating = rating;
        clearInterval(intervalId);
      }

      setDisplayedRating(Math.round(currentRating));
    }, 20);

    return () => clearInterval(intervalId);
  }, [rating]);

  useEffect(() => {
    if (rating > 0) {
      setVisibleGlobe(true);
    }
  }, [displayedRating, rating, setVisibleGlobe]);

  useEffect(() => {
    if (!keyWord) return;
    const controller = new AbortController()
    API.getRating({ detail: keyWord }, controller.signal)
      .then((res) => {
        const transformedData = res.data.map((log) => ({
          id: log.dettaglio,
          ...log,
        }));
        setDataTable(transformedData);
        setCharged(true);
      })
      .catch((err) => {
        if (err.name !== 'CanceledError') {
          console.error("[RatingGraph] Errore nel recupero dei dati:", err);
          setCharged(true);
        }
      });
    return () => controller.abort()
  }, [keyWord]);

  const TOCloseDialog = () => {
    setOpenDialog(false);
    setDataTable([]);
    setCharged(false);
  };

  const TOOpenDialog = () => {
    setOpenDialog(true);
  };

  function formatDate(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  }

  const handleLinkClick = (details, tipo) => {
    let item;

    if (tipo === 1) {
      item = { page: "Critical", url: "critical" };
    } else if (tipo === 0) {
      item = { page: "High", url: "high" };
    } else if (tipo === 2) {
      item = { page: "Alert", url: "alert" };
    } else if (tipo === 3) {
      item = { page: "Movements", url: "movements" };
    } else if (tipo === 4) {
      item = { page: "Threat Intelligence", url: "threat-intelligence" };
    }

    let dateTo = new Date();
    let dateFrom = new Date();

    if (sessionStorage.getItem("tipoLecs") === "LECS-CORE-SFP") {
      dateFrom.setTime(dateFrom.getTime() - 1 * 24 * 60 * 60 * 1000);
      dateFrom.setHours(0, 0, 0, 0);
    } else {
      dateFrom.setTime(dateFrom.getTime() - 7 * 24 * 60 * 60 * 1000);
      dateFrom.setHours(0, 0, 0, 0);
    }

    const formattedDateFrom = formatDate(dateFrom);
    const formattedDateTo = formatDate(dateTo);

    window.sessionStorage.setItem("selectedTab", item.page);

    if (window.sessionStorage.getItem("type") === "reseller") {
      navigate(
        `/reseller/myC/${item.url}?exact_details=${encodeURIComponent(
          details
        )}&dateFrom=${formattedDateFrom}&dateTo=${formattedDateTo}`
      );
    } else {
      window.location.href = `/user/security/${
        item.url
      }?exact_details=${encodeURIComponent(
        details
      )}&dateFrom=${formattedDateFrom}&dateTo=${formattedDateTo}`;
    }
  };

  const tableColumns = [
    {
      field: "tipo",
      headerName: "Type",
      headerAlign: "center",
      minWidth: 150,
      align: "center",
      renderCell: renderTypeCell,
    },
    {
      field: "count",
      headerName: "Count",
      headerAlign: "center",
      minWidth: 150,
      align: "center",
    },
    {
      field: "dettaglio",
      headerName: "Detail",
      headerAlign: "left",
      minWidth: 650,
      align: "left",
      renderCell: (params) => {
        const cellStyle = {
          color: TOEstablishTheColorOfLog(params.value),
        };

        return <div style={{ ...cellStyle, width: "auto" }}>{params.value}</div>;
      },
    },
    {
      field: "main-table",
      headerName: "MAIN TABLE",
      headerAlign: "center",
      flex: 1,
      minWidth: 150,
      align: "center",
      renderCell: (params) => (
        <Button
          sx={{
            backgroundColor: "#2d2d2d !important",
            color: "#eaeaea !important",
            fontWeight: 600,
            fontSize: 12,
            "&:hover": {
              backgroundColor: "#eaeaea !important",
              color: "#131C2D !important",
            },
          }}
          onClick={() =>
            handleLinkClick(params.row.dettaglio, params.row.tipo)
          }
        >
          Main Table
        </Button>
      ),
    },
  ];

  const iconSx = {
    fontSize: 16,
    color: "#888",
  };

  return (
    <>
      <Dialog
        open={openDialog}
        onClose={TOCloseDialog}
        sx={{
          "& .MuiDialog-paper": {
            width: "100%",
            height: "100%",
            maxWidth: "none",
            backgroundColor: "#4e4e4e",
            backgroundImage: "none",
          },
        }}
      >
        <IconButton onClick={TOCloseDialog}>
          <CloseIcon />
        </IconButton>

        <DialogContent>
          <DataGridPro
            columns={tableColumns}
            loading={!charged}
            rows={dataTable}
            sx={dataGridStyle}
          />
        </DialogContent>
      </Dialog>

      <Box
        sx={{
          ...boxStyle,
          p: 2,
          height: "100%",
          display: "flex",
          flexDirection: "column",

        }}
        className="ratingGraph"
      >
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 500, color: "#888" }}>
            Passive Vulnerabilities Score
          </Typography>

          {loading ? (
            <>
              <Box sx={{ display: "flex", justifyContent: "center", my: 3 }}>
                <Skeleton variant="circular" width={160} height={160} sx={{ bgcolor: "rgba(255,255,255,0.08)" }} />
              </Box>
              <Grid container spacing={1}>
                {[...Array(4)].map((_, i) => (
                  <Grid key={i} size={6}>
                    <Skeleton variant="rounded" height={48} sx={{ bgcolor: "rgba(255,255,255,0.08)" }} />
                  </Grid>
                ))}
              </Grid>
            </>
          ) : (
            <Grid container spacing={3} alignItems="center">
              <Grid size={12}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    marginTop: "20px",
                    marginBottom: "20px"
                  }}
                >
                  <CircularGauge
                    value={displayedRating}
                    color={barColor}
                    size={160}
                  />
                </Box>
              </Grid>

              <Grid size={6}>
                <Stack spacing={0.5}>
                  <StatRow
                    icon={<BugReportIcon sx={iconSx} />}
                    label="CVE's"
                    value={cve}
                    onClick={() => {
                      setKeyWord("cve");
                      TOOpenDialog();
                    }}
                  />
                  <StatRow
                    icon={<KeyIcon sx={iconSx} />}
                    label="Default Credentials"
                    value={def}
                    onClick={() => {
                      setKeyWord("default password|default username");
                      TOOpenDialog();
                    }}
                  />
                </Stack>
              </Grid>
              <Grid size={6}>
                <Stack spacing={0.5}>
                  <StatRow
                    icon={<ShieldIcon sx={iconSx} />}
                    label="Vulnerabilities"
                    value={vuln}
                    onClick={() => {
                      setKeyWord("vuln|exposure");
                      TOOpenDialog();
                    }}
                  />
                  <StatRow
                    icon={<WarningAmberIcon sx={iconSx} />}
                    label="Expired"
                    value={expire}
                    onClick={() => {
                      setKeyWord("expired|obsolete");
                      TOOpenDialog();
                    }}
                  />
                </Stack>
              </Grid>
            </Grid>
          )}
        </Box>

        <Typography sx={{ fontSize: 12, color: "#555",  position: 'absolute', bottom: '12px'}}>
          Here you will be able to find a value between 1 and 100 that
          represents an evaluation based on the analysis of passively observed
          vulnerabilities from the network analyzed by LECS.
        </Typography>
      </Box>
    </>
  );
}

export default RatingGraph;